import { Button, Checkbox, Dropdown, Input, Table, TableColumnsType, Tag, Tooltip, Typography } from "antd";
import { ApiService } from "api/api.service";
import CustomDateRange, { initialDateRange } from "components/CustomDateRange";
import PageTitle from "components/PageTitle";
import TextNumeric from "components/TextNumeric";
import { Assignment } from "dtos/leadCases.dto";
import DashboardLayout from "layout/DashboardLayout";
import { useState } from "react";
import { TbChevronLeft, TbChevronRight, TbColumns3, TbLoader2, TbSearch } from "react-icons/tb";
import { ASSIGNMENT_METRICS_CASE_STATUS_MAP, CASE_STATUS_COLOURS, CASE_STATUS_ICONS } from "utils/constants";
import { formatDuration } from "utils/functions";

// import styles from './AssignmentAll.module.scss';

type T_Params = Parameters<typeof ApiService.lead.useGetPaginatedAssignments>[0];

function AssignmentAll() {
  const [params, setParams] = useState<T_Params>({
    startDate: initialDateRange?.startDate,
    endDate: initialDateRange?.endDate,
  });
  const quoteList = ApiService.lead.useGetPaginatedAssignments(params);
  const [searchText, setSearchText] = useState<string>();

  const columns: TableColumnsType<Assignment> = [
    {
      key: "name",
      dataIndex: "name",
      title: "Assignee",
      filteredValue: searchText && searchText?.length > 0 ? ["name"] : null,
      onFilter: (_, record) => {
        return record.name?.toLowerCase()?.includes(searchText?.toLowerCase() as string);
      },
    },
    {
      key: "total_assigned",
      dataIndex: "total_assigned",
      title: "Total Assigned",
      width: 130,
      align: "right",
      render: (_, record) => {
        return <TextNumeric>{record?.total_assigned}</TextNumeric>;
      },
    },
    {
      key: "total_completed",
      dataIndex: "total_completed",
      title: "Total Completed",
      width: 130,
      align: "right",
      render: (_, record) => {
        return <TextNumeric>{record?.total_completed}</TextNumeric>;
      },
    },
    {
      key: "total_updated",
      dataIndex: "total_updated",
      title: "Total Updated",
      width: 130,
      align: "right",
      render: (_, record) => {
        return <TextNumeric>{record?.total_updated}</TextNumeric>;
      },
    },
    {
      key: "avg_first_update_time",
      dataIndex: "avg_first_update_time",
      title: "Avg First Update Time",
      width: 230,
      align: "right",
      render: (_, record) => {
        return <TextNumeric>{formatDuration(record?.avg_first_update_time)}</TextNumeric>;
      },
    },
    {
      key: "avg_last_update_time",
      dataIndex: "avg_last_update_time",
      title: "Avg Last Update Time",
      width: 230,
      align: "right",
      render: (_, record) => {
        return <TextNumeric>{formatDuration(record?.avg_last_update_time)}</TextNumeric>;
      },
    },
    {
      key: "metrics",
      title: "Metrics",
      width: 300,
      align: "center",
      render: (_, record) => {
        const keys: (keyof Assignment)[] = ["email_found", "phone_found", "both_found"];
        return (
          <div className="inline-grid grid-cols-[1fr_1fr_1fr] gap-6 items-center">
            {keys.map((key) => {
              const status = ASSIGNMENT_METRICS_CASE_STATUS_MAP[key];

              return (
                <Tooltip title={status?.toUpperCase()} key={key}>
                  <div className="inline-flex">
                    <Tag
                      className="size-5 inline-flex items-center justify-center !p-0 !m-0 !mr-1"
                      icon={status ? CASE_STATUS_ICONS[status] : null}
                      color={status ? CASE_STATUS_COLOURS[status] : "default"}
                    ></Tag>
                    <TextNumeric>
                      <Typography.Text className="font-bold">{record[key]}</Typography.Text>
                    </TextNumeric>
                  </div>
                </Tooltip>
              );
            })}
          </div>
        );
      },
    },
  ];

  const [showColumns, setShowColumns] = useState<Partial<Record<keyof Assignment | "metrics", boolean>>>({
    name: true,
    email: true,
    total_assigned: true,
    total_completed: true,
    total_updated: true,
    avg_first_update_time: true,
    avg_last_update_time: true,
    metrics: true,
  });

  return (
    <DashboardLayout
      headerLeft={
        <Typography.Title level={5} className="!m-0">
          Case Assignments Analytics
        </Typography.Title>
      }
    >
      <PageTitle title="Case Assignments Analytics" pageTitle={[{ title: "Assignments" }]} />
      <div className="flex justify-between gap-3 p-3">
        <div className="inline-flex gap-3 items-center">
          <Dropdown
            trigger={["click"]}
            menu={{
              items: columns.map((el) => {
                const key = String(el.key) as keyof typeof showColumns;
                return {
                  key: key,
                  label: (
                    <div className="flex gap-2" key={el.key}>
                      <Checkbox checked={showColumns[key]} />
                      <span>{el.title?.toString()}</span>
                    </div>
                  ),
                };
              }),
              onClick: ({ key }) => {
                setShowColumns((prev) => {
                  return { ...prev, [key]: !(prev as any)[key] };
                });
              },
            }}
          >
            <Button icon={<TbColumns3 />}>
              Columns{" "}
              <Tag bordered={false} className="!m-0 !bg-gray-100 !font-medium">
                {
                  columns.filter((el) => {
                    const key = String(el.key) as keyof typeof showColumns;
                    return showColumns[key];
                  }).length
                }
              </Tag>
            </Button>
          </Dropdown>
          {/* <Dropdown
            trigger={["click"]}
            menu={{
              // selectable: true,
              items: [
                {
                  type: "group",
                  // key: "status",
                  label: "Status",
                  children: Object.entries(LeadStatus).map(([key, value]) => ({
                    label: (
                      <div className="flex gap-2" key={key}>
                        <Checkbox checked={params?.filter?.status?.includes(value)} />
                        <Tag color={LEAD_STATUS_COLOURS[value]}>{key.replace(/_/g, " ")}</Tag>
                      </div>
                    ),
                    key: `status:${value}`,
                  })),
                },
                {
                  type: "divider",
                },
                {
                  type: "group",
                  // key: "is_making_payments",
                  label: "Currently Paying",
                  children: Object.values(MakingPayments).map((el) => {
                    return {
                      key: `questionaire.is_making_payments:${el}`,
                      label: (
                        <div className="flex gap-2" key={el}>
                          <Checkbox checked={params?.filter?.["questionaire.is_making_payments"]?.includes(el)} />
                          <span>{el}</span>
                        </div>
                      ),
                    };
                  }),
                },
                {
                  type: "divider",
                },
                {
                  type: "group",
                  // key: "payment_frequency",
                  label: "Payment Frequency",
                  children: Object.values(PaymentFrequency).map((el) => {
                    return {
                      key: `questionaire.payment_frequency:${el}`,
                      label: (
                        <div className="flex gap-2" key={el}>
                          <Checkbox checked={params?.filter?.["questionaire.payment_frequency"]?.includes(el)} />
                          <span>{el}</span>
                        </div>
                      ),
                    };
                  }),
                },
                {
                  type: "divider",
                },
                {
                  type: "group",
                  label: "Has Pending Lawsuit?",
                  children: ["Yes", "No"].map((el) => {
                    return {
                      key: `questionaire.has_pending_lawsuit:${el}`,
                      label: (
                        <div className="flex gap-2" key={el}>
                          <Checkbox checked={params?.filter?.["questionaire.has_pending_lawsuit"]?.includes(el)} />
                          <span>{el}</span>
                        </div>
                      ),
                    };
                  }),
                },
              ].map((el) => {
                if (el.type === "group") {
                  return {
                    ...el,
                    children: el.children?.map((child) => {
                      const [k, value] = child.key.split(":");
                      const key = k as keyof NonNullable<typeof params.filter>;

                      return {
                        ...child,
                        onClick: () => {
                          setParams((prev) => {
                            const prevArr = prev?.filter?.[key] ?? [];
                            let newArr: string[] = [];
                            if (prevArr.includes(value)) {
                              newArr = prevArr.filter((i) => i !== value);
                            } else {
                              newArr = [...prevArr, value];
                            }
                            return { ...prev, filter: { ...(prev?.filter ?? {}), [key]: newArr } };
                          });
                        },
                      };
                    }),
                  } as MenuItemGroupType<MenuItemType>;
                } else {
                  return el as MenuItemType;
                }
              }),
            }}
          >
            <Button icon={<LuListFilter />}>
              Filter{" "}
              {params.filter ? (
                <Tag bordered={false} className="!m-0 !bg-gray-100 !font-medium">
                  {
                    Object.values(params.filter)
                      .map((el) => el)
                      .flat()?.length
                  }
                </Tag>
              ) : null}
            </Button>
          </Dropdown> */}
          {/* <Space.Compact>
            <Dropdown
              trigger={["click"]}
              menu={{
                selectable: true,
                selectedKeys: params?.sort ? Object.keys(params.sort) : [],
                onSelect: ({ key }) => setParams((prev) => ({ ...prev, sort: { [key]: sortOrder } as any })),
                items: [
                  { key: "name", label: "Name" },
                  { key: "total_assigned", label: "Total Assigned" },
                  { key: "total_completed", label: "Total Completed" },
                  { key: "total_updated", label: "Total Updated" },
                  { key: "avg_first_update_time", label: "Avg First Update Time" },
                  { key: "avg_last_update_time", label: "Avg Last Update Time" },
                ],
              }}
            >
              <Button
                className="border-gray-300"
                variant={params?.sort ? "filled" : "outlined"}
                color={params?.sort ? "primary" : "default"}
                icon={<TbArrowsUpDown />}
              >
                Sort
              </Button>
            </Dropdown>
            {params?.sort && Object.keys(params?.sort)?.length > 0 ? (
              <>
                <Button
                  color="default"
                  className="border-gray-300 px-2"
                  icon={sortOrder === -1 ? <TbSortDescendingNumbers /> : <TbSortAscendingNumbers />}
                  onClick={() =>
                    setParams((prev) => {
                      if (!prev?.sort) return prev;
                      const key = Object.keys(prev?.sort)?.[0];
                      return { ...prev, sort: { [key]: sortOrder === 1 ? -1 : 1 } };
                    })
                  }
                >
                  <Typography.Text className="text-xs w-10">{sortOrder === -1 ? "DESC" : "ASC"}</Typography.Text>
                </Button>
                <Button icon={<TbX />} onClick={() => setParams((prev) => ({ ...prev, sort: undefined }))} />
              </>
            ) : null}
          </Space.Compact> */}
          <Typography.Text type="secondary">Period</Typography.Text>
          <CustomDateRange
            startDate={params.startDate}
            endDate={params.endDate}
            onChange={(opts) => {
              setParams({ startDate: opts?.startDate, endDate: opts?.endDate });
            }}
          />
        </div>
        <div className="inline-flex gap-3 items-center">
          <Input
            prefix={<TbSearch />}
            placeholder="Search assignees..."
            onPressEnter={(e) => {
              setSearchText(e.currentTarget.value);
            }}
            allowClear
            onClear={() => {
              setSearchText(undefined);
            }}
          />
        </div>
      </div>
      <Table<Assignment>
        scroll={{ x: "max-content", y: "calc(100vh - 202px)" }}
        virtual={false}
        size="small"
        loading={{
          spinning: quoteList.isFetching,
          indicator: <TbLoader2 size={48} className="text-brand-500 animate-spin" />,
        }}
        columns={columns.filter((el) => {
          const key = String(el.key) as keyof typeof showColumns;
          return key in showColumns && showColumns[key] == true;
        })}
        dataSource={quoteList.data}
        rowKey={"_id"} // Add this line
        pagination={{
          hideOnSinglePage: true,
          showQuickJumper: true,
          showPrevNextJumpers: true,
          nextIcon: (
            <Button
              className="ant-pagination-item-link !flex items-center justify-center"
              type="text"
              icon={<TbChevronRight size={20} />}
            />
          ),
          prevIcon: (
            <Button
              className="ant-pagination-item-link !flex items-center justify-center"
              type="text"
              icon={<TbChevronLeft size={20} />}
            />
          ),
          showTitle: true,
          showTotal: (total, range) => {
            return `Showing ${range[0]}-${range[1]} of ${total} leads`;
          },
          showSizeChanger: true,
          // total: quoteList.data?.totalObjects,
          // current: params.page,
          // pageSize: params.limit,
          // onChange: (page, pageSize) => {
          //   setParams({ page: page, limit: pageSize });
          // },
        }}
      />
    </DashboardLayout>
  );
}

export default AssignmentAll;
